import React from "react";
import styles from "./style.module.css";
import { useLocation } from "react-router-dom";
import { linkReplaceFn } from "../../utils/custom_funtions";
import logo from "../../assets/img/motion_new_logo.png";

const QuestionSolutionRender = () => {
  const location = useLocation();
  const responseData = location.state?.responseData || [];
  const language = location.state?.language || "english";

  // Extract options from HTML string
  const extractOptions = (htmlString) => {
    if (!htmlString) return [];
    return htmlString.split("@@@").map((html) => {
      const doc = new DOMParser().parseFromString(html, "text/html");
      return doc.body.textContent.trim(); // Extract text content from HTML
    });
  };

  // Group questions by exercise level and type with section persistence
  const groupByExerciseAndType = () => {
    const grouped = {};
    let currentExerciseLevel = null;
    let currentQuestionType = null;
    
    responseData.forEach(question => {
      // Update exercise level and question type only if they exist in the current question
      if (question.section) {
        currentExerciseLevel = question.section;
      }
      
      if (question.section_type) {
        currentQuestionType = question.section_type;
      }
      
      // Use current values or defaults if not set
      const exerciseLevel = currentExerciseLevel || "Exercise-1(Level A)";
      const questionType = currentQuestionType || "Single Correct";
      
      // Ensure nested objects exist
      if (!grouped[exerciseLevel]) {
        grouped[exerciseLevel] = {};
      }
      
      if (!grouped[exerciseLevel][questionType]) {
        grouped[exerciseLevel][questionType] = [];
      }
      
      // Add question to appropriate group
      grouped[exerciseLevel][questionType].push(question);
    });
    
    return grouped;
  };

  const groupedQuestions = groupByExerciseAndType();

  // Render a single question with solution
  const renderQuestion = (question, index) => {
    return (
      <div key={index} className={styles.questionItem}>
        {/* Question Row */}
       
        <div className={styles.questionRow1}>
          <div className={styles.questionNumberLine}>
            {index + 1}. Question
          </div>
          <div 
            className={styles.questionText} 
            dangerouslySetInnerHTML={{ 
              __html: linkReplaceFn(language === "english" ? question.cq_text : question.q_text_hindi) 
            }} 
          />
          
          {/* QR Code if available */}
          {question.qr_code && (
            <div className={styles.qrCode}>
              <img src={question.qr_code} alt="QR Code" />
            </div>
          )}
        </div>
        
        {/* Options in single column layout */}
        <div className={styles.optionsSingleColumn}>
          {/* Option A */}
          <div className={styles.singleOption}>
            <span className={styles.optionLabel}>(A)</span>
            <span className={styles.optionText}>
              {language === "english" 
                ? (extractOptions(question.eng_option)[0] || question.option_a)
                : (extractOptions(question.hindi_option)[0] || question.option_a_hindi)
              }
            </span>
          </div>
          
          {/* Option B */}
          <div className={styles.singleOption}>
            <span className={styles.optionLabel}>(B)</span>
            <span className={styles.optionText}>
              {language === "english" 
                ? (extractOptions(question.eng_option)[1] || question.option_b)
                : (extractOptions(question.hindi_option)[1] || question.option_b_hindi)
              }
            </span>
          </div>
          
          {/* Option C */}
          <div className={styles.singleOption}>
            <span className={styles.optionLabel}>(C)</span>
            <span className={styles.optionText}>
              {language === "english" 
                ? (extractOptions(question.eng_option)[2] || question.option_c)
                : (extractOptions(question.hindi_option)[2] || question.option_c_hindi)
              }
            </span>
          </div>
          
          {/* Option D */}
          <div className={styles.singleOption}>
            <span className={styles.optionLabel}>(D)</span>
            <span className={styles.optionText}>
              {language === "english" 
                ? (extractOptions(question.eng_option)[3] || question.option_d)
                : (extractOptions(question.hindi_option)[3] || question.option_d_hindi)
              }
            </span>
          </div>
        </div>
        
        {/* Answer Section */}
        <div className={styles.answerSection}>
          <h4 className={styles.answerTitle}>Answer:</h4>
          <div className={styles.answerText}>
            { question.answer}
          </div>
        </div>
        
        {/* Solution Section */}
        <div className={styles.solutionSection}>
          <h4 className={styles.solutionTitle}>Solution:</h4>
          <div 
            className={styles.solutionText}
            dangerouslySetInnerHTML={{ 
              __html: linkReplaceFn(language === "english" ? question.solution : question.solution_hin) 
            }}
          />
        </div>
      </div>
    );
  };

  
  const getTopicNameForSection = (questions) => {
    if (questions && questions.length > 0) {
      return questions[0].topic_name || "";
    }
    return "";
  };

  return (
    <div className={`${styles.container} container-fluid ${styles.printable}`}>
      <div className={styles.printHead}>
      <div className={styles.logoSection}>
        <img src={logo} alt="Motion" />
      </div>
      <h5 className={styles.title}>
        <b>Sample Question</b>
      </h5>
      <h4 className={styles.subName}>
        <b>{responseData.length > 0 ? responseData[0].sub_name : ""}</b>
      </h4>
      </div>
      <hr className={styles.headerHr} />
      
      {Object.entries(groupedQuestions).map(([exerciseLevel, questionTypes], index) => (
        <div key={exerciseLevel} className={`${styles.exerciseSection} ${index === 0 ? styles.firstExerciseSection : ''}`}>
          {/* Dots and Line Header */}
          <div className={styles.exerciseHeader}>
            <div className={styles.exerciseDots}>•••</div>
            <div className={styles.redLine} />
          </div>
          
          {/* Exercise Title Row */}
          <div className={styles.exerciseTitleRow}>
            <div className={styles.exerciseTitle}>{exerciseLevel}</div>
            <div className={styles.questionType}>
              {Object.keys(questionTypes)[0] || "Single Correct"}
            </div>
          </div>
          
          <div className={styles.redLine} />
          
          {/* Questions by type */}
          {Object.entries(questionTypes).map(([type, questions]) => {
            // Get topic name from first question in this section
            const topicName = getTopicNameForSection(questions);
            
            return (
              <div key={type}>
                {/* Subject/Topic Name */}
                {topicName && (
                  <div className={styles.subjectName}>
                    {topicName}
                  </div>
                )}
               
                
                {/* Questions in two columns */}
                <div className={styles.printOptimizedLayout}>
                  {questions.map((question, index) =>
                    renderQuestion(question, index)
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default QuestionSolutionRender;